import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Search, ArrowUpRight } from "lucide-react";
import { SEARCH_INDEX } from "@/data/searchIndex";

const SUGGESTIONS = [
  "Ghana–Jamaica Relations",
  "Reparatory Justice",
  "AfCFTA–CARICOM",
  "Climate Resilience",
  "Health Workforce",
  "Creative Economy",
];

const slugify = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

export const SearchOverlay = ({ open, onClose }) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      const t = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const tokens = q.split(/\s+/);
    return SEARCH_INDEX.filter((e) =>
      tokens.every((t) => `${e.title} ${e.keywords}`.toLowerCase().includes(t))
    )
      .sort((a, b) => Number(b.title.toLowerCase().includes(q)) - Number(a.title.toLowerCase().includes(q)))
      .slice(0, 8);
  }, [query]);

  if (!open) return null;

  return (
    <div data-testid="search-overlay" className="fixed inset-0 z-[70] overflow-y-auto bg-atlantic/95 backdrop-blur-sm">
      <button
        data-testid="search-close-button"
        onClick={onClose}
        aria-label="Close search"
        className="absolute right-6 top-6 text-white/70 transition-colors hover:text-gold"
      >
        <X size={26} />
      </button>
      <div className="mx-auto max-w-3xl px-6 pb-16 pt-24 sm:pt-32">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Search Atlantic Bridge</p>
        <div className="mt-6 flex items-center gap-4 border-b border-white/25 pb-4">
          <Search className="shrink-0 text-white/50" size={22} />
          <input
            ref={inputRef}
            data-testid="search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search reporting, research and countries…"
            className="w-full bg-transparent font-serif text-xl text-white placeholder:text-white/35 focus:outline-none sm:text-2xl"
          />
        </div>

        {query.trim() === "" ? (
          <>
            <div className="mt-8 flex flex-wrap gap-3">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid={`search-suggestion-${slugify(s)}`}
                  onClick={() => setQuery(s)}
                  className="border border-white/20 px-4 py-2 text-xs tracking-wide text-white/75 transition-colors hover:border-gold hover:text-gold"
                >
                  {s}
                </button>
              ))}
            </div>
            <p className="mt-10 text-xs leading-relaxed text-white/40">
              Suggestions reflect our current coverage areas. Results search across pages, dossiers, desks and series.
            </p>
          </>
        ) : results.length > 0 ? (
          <div data-testid="search-results" className="mt-8 divide-y divide-white/10 border-y border-white/10">
            {results.map((r, i) => (
              <Link
                key={`${r.to}-${r.title}`}
                to={r.to}
                onClick={onClose}
                data-testid={`search-result-${i}`}
                className="group flex items-center justify-between gap-4 py-4"
              >
                <span>
                  <span className="block text-[10px] font-semibold uppercase tracking-[0.25em] text-gold">{r.type}</span>
                  <span className="mt-1 block font-serif text-lg leading-snug text-white transition-colors group-hover:text-gold">
                    {r.title}
                  </span>
                </span>
                <ArrowUpRight size={16} className="shrink-0 text-white/35 transition-colors group-hover:text-gold" />
              </Link>
            ))}
          </div>
        ) : (
          <p data-testid="search-no-results" className="mt-10 text-sm text-white/60">
            No results for “{query.trim()}”. Try a country, topic or research area.
          </p>
        )}
      </div>
    </div>
  );
};
